/**
 * @fileoverview Shared Embedding schema used by both frontend and backend.
 * Queue table for async embedding generation.
 */

import { z } from 'zod';
import { commonValidators, createCrudSchemas, createEntityIdSchema, createOptionalDatetimeSchema, createdAtSchema, getSchemaFields, updatedAtSchema } from './base.js';
import { EmbeddingStatusEnum, ID_PATTERNS } from './types.js';

/**
 * Embedding validation schema (queue table)
 */
export const EmbeddingSchema = z.object({
  id: createEntityIdSchema(ID_PATTERNS.EMBEDDING, 'ID must be in embedding nanoid7 format (em + digit 2-9 + 4 alphanumeric)'),
  note_id: commonValidators.noteForeignKey('Note ID'),
  status: EmbeddingStatusEnum.default('pending'),
  error_message: z.string()
    .transform((val) => (val === '' ? null : val))
    .pipe(z.string().nullable())
    .optional()
    .nullable(),
  retry_count: z.number()
    .int('Retry count must be an integer')
    .min(0, 'Retry count cannot be negative')
    .max(10, 'Retry count cannot exceed 10')
    .default(0),
  processing_started_at: createOptionalDatetimeSchema('Processing started at'),
  completed_at: createOptionalDatetimeSchema('Completed at'),
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
});

// Embedding Create only requires note_id - all other fields are system-managed
const { CreateSchema } = createCrudSchemas(EmbeddingSchema, {
  additionalOmit: ['status', 'error_message', 'retry_count', 'processing_started_at', 'completed_at'],
});

// Embedding Update allows updating system fields (status, error_message, etc.)
const EmbeddingUpdateBase = EmbeddingSchema.omit({ id: true, created_at: true });

export const EmbeddingCreateSchema = CreateSchema;
export const EmbeddingUpdateSchema = EmbeddingUpdateBase.partial();

export type Embedding = z.infer<typeof EmbeddingSchema>;
export type EmbeddingCreate = z.infer<typeof EmbeddingCreateSchema>;
export type EmbeddingUpdate = z.infer<typeof EmbeddingUpdateSchema>;

export const EMBEDDING_FIELDS = getSchemaFields(EmbeddingSchema);
